import { BaselineDefinition } from '../model/baseline-definition.model';
import { Site } from '../model/site.model';
import { Registration } from '../model/registration.model';

export function filterActiveSDPs(sdps: any[]): any[] {
  if (!Array.isArray(sdps)) {
    return [];
  }
  return sdps.filter(
    sdp => Array.isArray(sdp.serviceAccounts) && sdp.serviceAccounts.some((sa: any) => sa.status === 'ACTIVE'),
  );
}

export function getSelectedSiteByIdFromData(appData: any, siteId: string): Site | undefined {
  if (!appData?.sites || !Array.isArray(appData.sites) || !siteId) {
    return undefined;
  }
  return appData.sites.find((s: Site) => s.id === siteId);
}

export function computeDefinitionDisplayLabel(
  registration: { baselineDefId?: string },
  baselineDefinitions: BaselineDefinition[] | undefined,
): string {
  if (!baselineDefinitions || baselineDefinitions.length === 0) {
    return 'registration.no_baseline_definitions';
  }
  if (registration?.baselineDefId) {
    const selected = baselineDefinitions.find(def => def.id === registration.baselineDefId);
    if (selected) {
      return selected.displayLabel || selected.blTemplateName;
    }
    return 'registration.baseline_definition_id_not_found';
  }
  const defaultDef = baselineDefinitions.find(def => def.defaultInd === true);
  if (defaultDef) {
    return defaultDef.displayLabel || defaultDef.blTemplateName;
  }
  return 'registration.default_baseline_definition_not_found';
}

export function computeBaselineTooltipKey(
  registration: { baselineDefId?: string },
  baselineDefinitions: BaselineDefinition[] | undefined,
): { key: string; isError: boolean } {
  if (!baselineDefinitions || baselineDefinitions.length === 0) {
    return { key: 'registration.tooltip.no_definitions_configured', isError: true };
  }
  if (registration?.baselineDefId) {
    const byId = baselineDefinitions.find(def => def.id === registration.baselineDefId);
    if (byId) {
      return { key: 'registration.tooltip.baseline_definitions', isError: false };
    }
    return { key: 'registration.tooltip.no_baseline_definition_found', isError: true };
  }
  const defaults = baselineDefinitions.filter(def => def.defaultInd === true);
  if (defaults.length === 0) {
    return { key: 'registration.tooltip.no_default_definition_found', isError: true };
  }
  if (defaults.length > 1) {
    return { key: 'registration.tooltip.more_than_one_default_definition', isError: true };
  }
  return { key: 'registration.tooltip.baseline_definitions', isError: false };
}

export function computeSdpServiceAccountLabel(
  sdps: any[],
  sdpId: string,
  serviceAccountId: string,
): string | undefined {
  if (!Array.isArray(sdps) || !sdpId || !serviceAccountId) {
    return undefined;
  }
  const sdp = sdps.find(s => s.id === sdpId);
  if (!sdp || !Array.isArray(sdp.serviceAccounts)) {
    return undefined;
  }
  const sa = sdp.serviceAccounts.find((a: any) => a.id === serviceAccountId);
  return sa?.displayLabel;
}

export function decideRegistrationTimezone(
  selectedSite: Pick<Site, 'timezone'> | undefined,
  mode: 'create' | 'edit' | 'view',
  isFirstTime: boolean,
  currentTimezone?: Registration['timezone'],
): Registration['timezone'] {
  if (!selectedSite?.timezone) {
    return currentTimezone ?? null;
  }
  if (mode === 'create') {
    return selectedSite.timezone;
  }
  if (mode === 'edit') {
    if (!isFirstTime) {
      return selectedSite.timezone;
    }
    return currentTimezone ?? selectedSite.timezone;
  }
  return currentTimezone ?? null;
}
